import type { IGhToolLabel } from '../gpthelp-host-contract';

/** Label groups of the tools with an exact entry (plan §9.5). */
const EXACT_LABELS: Record<string, string> = {
  stats_products_rank: 'productsRank',
  menu_proposeChanges: 'propose',
  // Not the stock reads' «Revisando su inventario» (plan P5-3).
  stock_proposeChanges: 'proposeStock',
  ui_navigate: 'navigate',
  // «Preparando el mensaje»: it shows the support card (plan §8 R4).
  support_handoff: 'support',
  assistant_listChanges: 'changes',
  // A file, like export_generateExcel (the other menu reads go by prefix).
  menu_exportCatalog: 'export',
};

/** Label groups by tool name prefix. */
const PREFIX_LABELS: Array<[string, string]> = [
  ['menu_', 'menu'],
  ['stats_', 'stats'],
  ['orders_', 'orders'],
  ['caja_', 'caja'],
  ['dayclose_', 'dayclose'],
  ['stock_', 'stock'],
  ['reservations_', 'reservations'],
  ['club_', 'club'],
  ['customers_', 'customers'],
  ['coupons_', 'coupons'],
  ['tables_', 'tables'],
  ['store_', 'store'],
  ['export_', 'export'],
];

/**
 * Groups whose «Revisé: …» subject is another group's, or none. Preparing a
 * change checks the menu: a turn whose only step is the proposal reads
 * «Revisé: su menú», and the widget drops a repeated subject, so a
 * read-then-propose turn never reads «su menú, los cambios». Opening a page
 * or writing a message to support checks nothing, so the widget leaves it
 * out.
 */
const SUBJECT_GROUPS = new Map<string, string | null>([
  ['propose', 'menu'],
  // Preparing an inventory change checks the inventory (plan P5-3).
  ['proposeStock', 'stock'],
  ['navigate', null],
  ['support', null],
]);

function labelGroup(name: string): string | null {
  if (EXACT_LABELS[name]) return EXACT_LABELS[name];
  const match = PREFIX_LABELS.find(([prefix]) => name.startsWith(prefix));
  return match ? match[1] : null;
}

function subjectGroup(group: string): string | null {
  const mapped = SUBJECT_GROUPS.get(group);
  return mapped === undefined ? group : mapped;
}

/**
 * «Revisando sus ventas» while a tool runs and «sus ventas» after it, for
 * each tool name (plan §9.5); tools without a group keep the widget's own
 * wording.
 */
export function assistantToolLabels(
  names: string[],
  t: (key: string) => string
): Record<string, IGhToolLabel> {
  const labels: Record<string, IGhToolLabel> = {};
  for (const name of names) {
    const group = labelGroup(name);
    if (!group) continue;
    const subject = subjectGroup(group);
    labels[name] = {
      progress: t(`assistant.labels.${group}.progress`),
      subject: subject ? t(`assistant.labels.${subject}.subject`) : '',
    };
  }
  return labels;
}
